import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import Tabs from '../../components/tabs/Tabs';
import { getProtectorDetail } from '../../services/GuaranteesService';
import { routes } from '../../routes/routePaths';
import ProtectorDetailsBasicSection from './ProtectorDetailsBasicSection';
import ProtectorDetailsContributionsSection from './ProtectorDetailsContributionsSection';
import ProtectorDetailsShareReceiptsSection from './ProtectorDetailsShareReceiptsSection';
import ProtectorDetailsDefaultSection from './ProtectorDetailsDefaultSection';

interface ProtectorDetailData {
  id: number;
  company_name: string;
  document_type_id: number;
  document_type_name: string;
  legal_entity_type_id: number;
  legal_entity_type_name: string;
  cuit: string;
  email: string;
  meeting_minute_type_id: number;
  meeting_minute_type_name: string;
  minute_number: string;
  registration_date: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

const ProtectorsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [protectorData, setProtectorData] = useState<ProtectorDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchProtectorDetail();
    }
  }, [id]);

  const fetchProtectorDetail = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProtectorDetail(id);
      if (response.success && 'data' in response) {
        setProtectorData(response.data);
      } else {
        setError('Error al cargar los datos del protector');
      }
    } catch (err) {
      setError('Error al cargar los datos del protector');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.fundsAndProtectors);
  };

  const handleNewContribution = () => {
    if (id) {
      navigate(`/protectores/${id}/aportes/new`);
    }
  };

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <TableSkeleton rows={10} columns={4} />
      </Card>
    );
  }

  if (error || !protectorData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Protector no encontrado'}</P>
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card display='flex' alignItems='center' justifyContent='space-between' margin={{ bottom: '4' }}> 
        <Card display='flex' alignItems='center' gap='16px'>
          <Button 
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a protectores"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Detalle protector
          </H>
        </Card>
      </Card>
      
      <ProtectorDetailsBasicSection protector={protectorData} />

      <Tabs
        tabs={[
          {
            label: 'Aportes',
            content: (
              <ProtectorDetailsContributionsSection 
                protectorId={id}
                onNewContribution={handleNewContribution}
              />
            )
          },
          {
            label: 'Cesiones',
            content: (
              <ProtectorDetailsShareReceiptsSection 
                protectorId={id}
              />
            )
          },
          {
            label: 'Mora',
            content: (
              <ProtectorDetailsDefaultSection 
                protectorId={id}
              />
            )
          }
        ]}
        defaultActiveTab={0}
      />
    </Card>
  );
};

export default ProtectorsDetails;

